import { useState, useCallback } from 'react'

export interface PagedListBaseParams {
  page: number
  limit: number
}

export interface UsePagedListParamsReturn<T extends PagedListBaseParams> {
  /** Current params object — pass directly to your list hook. */
  params: T
  /** Navigate to a specific page without touching filters. */
  setPage: (page: number) => void
  /** Change page size and reset to page 1. */
  setLimit: (limit: number) => void
  /**
   * Apply one or more filter fields. Always resets page to 1 and preserves
   * the current limit unless you explicitly override it.
   */
  applyFilters: (filters: Partial<Omit<T, 'page'>>) => void
  /** Clear all filters back to the initial defaults. */
  reset: () => void
}

/**
 * Generic hook that centralises page / limit / filter state for server-paginated lists.
 *
 * Keeps the "reset page to 1 on every filter change" rule in one place so
 * individual page components don't have to repeat it.
 *
 * @example
 * const { params, setPage, applyFilters, reset } = usePagedListParams<UserListQueryParams>({
 *   page: 1,
 *   limit: 10,
 * })
 * const { data } = useUsersList(params)
 */
export function usePagedListParams<T extends PagedListBaseParams>(
  defaults: T,
): UsePagedListParamsReturn<T> {
  const [params, setParams] = useState<T>(defaults)

  const setPage = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }))
  }, [])

  const setLimit = useCallback((limit: number) => {
    setParams((prev) => ({ ...prev, limit, page: 1 }))
  }, [])

  const applyFilters = useCallback(
    (filters: Partial<Omit<T, 'page'>>) => {
      setParams((prev) => ({ ...prev, ...filters, page: 1 }))
    },
    [],
  )

  const reset = useCallback(() => {
    setParams(defaults)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { params, setPage, setLimit, applyFilters, reset }
}
